import { useCallback } from 'react';
import { fetchApi } from 'react-scale/redux/actions';
import axios from 'axios';
import pMap from 'p-map';
import { captureClientSideError } from 'react-scale/lib/errors';

const CDS_CHUNK_SIZE = 20 * 1024 * 1024;
export interface CDSMultipartUploadHook {
  upload: ({
    name,
    file,
    onProgress,
    onComplete,
    onError,
  }: {
    name: string;
    file: File;
    onProgress?: (progress: number) => void;
    onComplete?: (cdsUrl: string) => void;
    onError?: (message: string) => void;
  }) => Promise<string>;
}

const useCDSMultipartUpload = (subtaskId?: string, userId?: string): CDSMultipartUploadHook => {
  const executeMultipartUpload = useCallback(
    async ({
      file,
      onProgress,
      onComplete,
      onError,
    }: {
      file: File;
      onProgress?: (progress: number) => void;
      onComplete?: (cdsUrl: string) => void;
      onError?: (message: string) => void;
    }): Promise<string> => {
      const attachmentSize = file.size;
      const contentType = file.type;
      const { cdsUrl, partUrls, uploadId } = await fetchApi(
        `/multipartupload/startCDSMultipartUpload`,
        {
          body: {
            attachmentSize,
            contentType,
            subtaskId,
          },
          maxRetries: 3,
          method: 'POST',
          parseAndHandle: true,
          redirectOnUnauthorized: true,
        },
      );

      const bodyParts: Blob[] = [];
      for (let index = 0; index < partUrls.length; index++) {
        const start = index * CDS_CHUNK_SIZE;
        const end = (index + 1) * CDS_CHUNK_SIZE;
        bodyParts.push(file.slice(start, end));
      }
      const headers = { 'content-type': 'application/octet-stream' };

      function delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
      }

      let progress = 0;
      const parts = await pMap(
        partUrls,
        async (url: string, index: number) => {
          let partUrl = url;
          const partNumber = index + 1;
          for (let i = 0; i < 20; i++) {
            try {
              const response = await axios.put(partUrl as string, bodyParts[index], {
                headers,
                maxBodyLength: Infinity,
                maxContentLength: Infinity,
              });

              if (response.status < 200 || response.status > 299) {
                throw new Error(
                  `Response for upload of partUrl ${partUrl} was not successful, status code: ${response.status}`,
                );
              }

              const eTag = response.headers.etag;
              progress += 1 / partUrls.length;
              onProgress && onProgress(progress);
              return { eTag, partNumber };
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn(
                `Attempt ${i} to upload ${partUrl} failed with ${JSON.stringify(e)}, retrying...`,
              );
              captureClientSideError(
                new Error(
                  `Error occurred during frontend CDS GenericDataCollectionAttempt uploading, try number ${i}. Error is: ${JSON.stringify(
                    e,
                  )}`,
                ),
                'genericdatacollection',
                {
                  subtaskId,
                  userId,
                  e,
                },
              );
              delay(1000);

              const { partUrl: newPartUrl } = await fetchApi(
                `/multipartupload/retrieveCDSPartUrl`,
                {
                  body: {
                    cdsUrl,
                    partNumber,
                    uploadId,
                    subtaskId,
                  },
                  maxRetries: 3,
                  method: 'POST',
                  parseAndHandle: true,
                  redirectOnUnauthorized: true,
                },
              );

              partUrl = newPartUrl;

              continue;
            }
          }

          onError && onError('Failed upload. Please retry.');
          throw Error('Failed upload. Please retry.');
        },
        { concurrency: 100 },
      );

      await fetchApi(`/multipartupload/completeCDSMultipartUpload`, {
        body: {
          uploadId,
          parts,
          subtaskId,
        },
        maxRetries: 3,
        method: 'POST',
        parseAndHandle: true,
        redirectOnUnauthorized: true,
      });
      onComplete && onComplete(cdsUrl);
      return cdsUrl;
    },
    [subtaskId],
  );

  const upload = useCallback(
    async ({
      name,
      file,
      onProgress,
      onComplete,
      onError,
    }: {
      name: string;
      file: File;
      onProgress?: (progress: number) => void;
      onComplete?: (cdsUrl: string) => void;
      onError?: (message: string) => void;
    }): Promise<string> => {
      if (!name || !file) {
        throw Error('Name and file must be provided to upload a file');
      }

      return executeMultipartUpload({
        file,
        onProgress,
        onComplete,
        onError,
      });
    },
    [executeMultipartUpload],
  );

  return {
    upload,
  };
};

export default useCDSMultipartUpload;
